import React from 'react';
import { Clock, CheckCircle, AlertCircle, ExternalLink, ArrowDownLeft, ArrowUpRight } from 'lucide-react';

const TransactionHistoryList = ({
  deposits,
  withdrawals,
  t,
  isMobile = false
}) => {
  const getStatusStyle = (status, type = 'deposit') => {
    const isApproved = status === 'approved' || status === 'completed';
    const isRejected = status === 'rejected';
    
    return {
      padding: isMobile ? '4px 8px' : '4px 8px',
      borderRadius: '4px',
      fontSize: isMobile ? '10px' : '11px',
      fontWeight: '600',
      backgroundColor: isApproved ? '#0ecb8120' : isRejected ? '#f6465d20' : '#f0b90b20',
      color: isApproved ? '#0ecb81' : isRejected ? '#f6465d' : '#f0b90b',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    };
  };

  const getStatusText = (status, type = 'deposit') => {
    if (status === 'approved' || status === 'completed') return t('completed') || 'Completed';
    if (status === 'rejected') return t('failed') || 'Failed';
    return t('pending') || 'Pending';
  };

  if (isMobile) {
    return (
      <div>
        {/* Deposits Section */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '12px' 
          }}>
            <ArrowDownLeft size={16} color="#0ecb81" />
            <span style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>
              {t('depositHistory')} ({deposits.length})
            </span>
          </div>
          
          {deposits.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '24px', 
              color: '#848e9c',
              backgroundColor: '#1e2329',
              borderRadius: '10px',
            }}>
              {t('noWallets') || 'No deposits yet'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {deposits.slice(0, 5).map((deposit) => (
                <div
                  key={deposit.id}
                  style={{
                    backgroundColor: '#1e2329',
                    borderRadius: '10px',
                    padding: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#0ecb81' }}>
                      +{deposit.amount} {deposit.currency}
                    </div>
                    <div style={{ fontSize: '11px', color: '#848e9c', marginTop: '2px' }}>
                      {new Date(deposit.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={getStatusStyle(deposit.status, 'deposit')}>
                    {getStatusText(deposit.status, 'deposit')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Withdrawals Section */}
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '12px' 
          }}>
            <ArrowUpRight size={16} color="#f6465d" />
            <span style={{ color: '#eaecef', fontSize: '14px', fontWeight: '600' }}>
              {t('withdrawalHistory')} ({withdrawals.length})
            </span>
          </div>
          
          {withdrawals.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '24px', 
              color: '#848e9c',
              backgroundColor: '#1e2329',
              borderRadius: '10px',
            }}>
              {t('noWithdrawalsYet') || 'No withdrawals yet'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {withdrawals.slice(0, 5).map((withdrawal) => (
                <div
                  key={withdrawal.id}
                  style={{
                    backgroundColor: '#1e2329',
                    borderRadius: '10px',
                    padding: '12px',
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '4px',
                  }}>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#f6465d' }}>
                      -{withdrawal.amount} {withdrawal.currency}
                    </div>
                    <div style={getStatusStyle(withdrawal.status, 'withdrawal')}>
                      {getStatusText(withdrawal.status, 'withdrawal')}
                    </div>
                  </div>
                  <div style={{ fontSize: '10px', color: '#848e9c' }}>
                    {withdrawal.address?.slice(0, 12)}...{withdrawal.address?.slice(-6)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop version
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', height: '100%' }}>
      {/* Deposits */}
      <div style={{
        backgroundColor: '#1e2329',
        border: '1px solid #2b3139',
        borderRadius: '12px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '400px',
      }}>
        <h3 style={{ color: '#eaecef', fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <ArrowDownLeft size={18} color="#0ecb81" />
          {t('depositHistory')} ({deposits.length})
        </h3>
        {deposits.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#848e9c' }}>
            {t('noWallets') || 'No deposits yet'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', flex: 1 }}>
            {deposits.map((deposit) => (
              <div key={deposit.id} style={{
                backgroundColor: '#181a20',
                border: '1px solid #2b3139',
                borderRadius: '8px',
                padding: '16px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#0ecb81' }}>
                    +{deposit.amount} {deposit.currency}
                  </div>
                  <div style={getStatusStyle(deposit.status, 'deposit')}>
                    {deposit.status === 'pending' && <Clock size={12} />}
                    {deposit.status === 'approved' && <CheckCircle size={12} />}
                    {deposit.status === 'rejected' && <AlertCircle size={12} />}
                    {getStatusText(deposit.status, 'deposit')}
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#848e9c' }}>
                  {new Date(deposit.created_at).toLocaleString()}
                </div>
                {deposit.tx_hash && (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#848e9c', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    TX: {deposit.tx_hash.slice(0, 20)}...
                    <ExternalLink size={10} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Withdrawals */}
      <div style={{
        backgroundColor: '#1e2329',
        border: '1px solid #2b3139',
        borderRadius: '12px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '400px',
      }}>
        <h3 style={{ color: '#eaecef', fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <ArrowUpRight size={18} color="#f6465d" />
          {t('withdrawalHistory')} ({withdrawals.length})
        </h3>
        {withdrawals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#848e9c' }}>
            {t('noWithdrawalsYet') || 'No withdrawals yet'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', flex: 1 }}>
            {withdrawals.map((withdrawal) => (
              <div key={withdrawal.id} style={{
                backgroundColor: '#181a20',
                border: '1px solid #2b3139',
                borderRadius: '8px',
                padding: '16px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#f6465d' }}>
                    -{withdrawal.amount} {withdrawal.currency}
                  </div>
                  <div style={getStatusStyle(withdrawal.status, 'withdrawal')}>
                    {getStatusText(withdrawal.status, 'withdrawal')}
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#848e9c' }}>
                  {new Date(withdrawal.created_at).toLocaleString()}
                </div>
                <div style={{ marginTop: '4px', fontSize: '11px', color: '#848e9c' }}>
                  To: {withdrawal.address?.slice(0, 12)}...{withdrawal.address?.slice(-6)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistoryList;
